<template>
  <b-form-group
    :label="tabSettings.name"
    label-size="lg"
    data-test="tab-name-label"
  >
    <b-form-group
      label="Colour:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0"
    >
      <ColorPicker
        :required="true"
        :value="tabSettings.colour"
        :debounce="500"
        data-test="tab-colour-picker"
        @input="updateColour($event)"
      />
    </b-form-group>

    <b-form-group
      v-if="customiseTabFontColour"
      label="Font Colour:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0 pt-1"
    >
      <ColorPicker
        :value="tabSettings.fontColour "
        @input="updateTabFontColour($event)"
      />
    </b-form-group>

    <b-form-group
      v-if="showIcon"
      label="Icon:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0"
    >
      <ImageUpload
        class="pt-1"
        :config-path="[tab.path, 'icon']"
        :is-config="false"
      />
    </b-form-group>

    <b-form-group
      label="Label:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0 pt-1"
    >
      <b-input
        :placeholder="tabSettings.name"
        :value="tabSettings.label"
        data-test="tab-label-input"
        @input="updateLabel($event)"
      />
    </b-form-group>

    <b-form-group
      label="Visible:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0"
    >
      <div class="h-100 d-flex align-items-center">
        <b-checkbox
          :checked="tabSettings.visible"
          switch
          data-test="tab-visible-input"
          @change="updateVisible($event)"
        />
      </div>
    </b-form-group>

    <b-form-group
      v-if="showRemoveCustomTab"
      class="mb-0 pt-1"
    >
      <b-button
        variant="danger"
        block
        data-test="remove-custom-tab"
        @click="removeCustomTab"
      >
        Remove Custom Tab
      </b-button>
    </b-form-group>
  </b-form-group>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import { ColorPicker } from '@orbica/vue-modules'
import ImageUpload from '@/components/builder/images/ImageUpload.vue'
import BubbleParamSlider from '@/components/builder/BubbleParams/BubbleParamSlider.vue'

import { NODE_CONFIG_KEY } from '@/constants'
import { NodeConfig, NodeTypes, Tab } from '@/types'

export interface TabSettingsItem {
  name: NodeConfig['name'],
  colour: NodeConfig['colour'],
  path: string,
  label?: NodeConfig['label'],
  fontColour?: NodeConfig['fontColour'],
  nodeType: NodeConfig['nodeType'],
  visible: NodeConfig['visible'],
}

const components = {
  ImageUpload,
  ColorPicker,
  BubbleParamSlider,
}
const props = {
  tab: {
    type: Object as PropType<Tab>,
    required: true,
  },
}

@Component({ components })
export default class TabIconColourSetting extends Vue.extend({ props }) {
  get tabSettings (): TabSettingsItem {
    return {
      name: this.name(this.tab.path),
      colour: this.colour(this.tab.path),
      path: this.tab.path,
      label: this.label(this.tab.path),
      fontColour: this.fontColour(this.tab.path),
      visible: this.visible(this.tab.path),
      nodeType: this.nodeType(this.tab.path),
    }
  }

  get name () {
    return this.$typedStore.primary.entities.nodes.name
  }

  get colour () {
    return this.$typedStore.primary.entities.nodes.colour
  }

  get label () {
    return this.$typedStore.primary.entities.nodes.label
  }

  get fontColour () {
    return this.$typedStore.primary.entities.nodes.fontColour
  }

  get visible () {
    return this.$typedStore.primary.entities.nodes.visible
  }

  get nodeType () {
    return this.$typedStore.primary.entities.nodes.nodeType
  }

  get customiseTabFontColour () {
    return this.$typedStore.primary.app.config.appConfig?.customiseTabFontColour ?? false
  }

  get showIcon () {
    return this.$typedStore.primary.app.config.appConfig?.showIcon ?? true
  }

  get showRemoveCustomTab () {
    return this.tabSettings.nodeType === NodeTypes.CUSTOM
  }

  removeCustomTab () {
    this.$typedStore.primary.entities.tabs.removeCustomTab(this.tab.path)
  }

  updateColour (value: string) {
    this.updateNodeProp(value, 'colour')
  }

  updateTabFontColour (value: string) {
    this.updateNodeProp(value, 'fontColour')
  }

  updateLabel (value: string) {
    const label = value.length > 0 ? value : null
    this.updateNodeProp(label, 'label')
  }

  updateVisible (value: boolean) {
    this.$typedStore.primary.entities.tabs.setTabVisibility({ tab: this.tab, value })
  }

  updateNodeProp (value: string | null, ...args: [keyof NodeConfig] | []) {
    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.tab.path,
      configPath: [NODE_CONFIG_KEY, ...args],
      value,
    })
  }
}

</script>
